<script setup lang="ts">
	import LoadingModal from '@/components/LoadingModal.vue'
	import RoundedBox from '@/components/RoundedBox.vue'
	import StyledDatedMediaInput from '@/components/StyledDatedMediaInput.vue'

	import useAuthUser from '@/composables/useAuthUser'

	import useDocumentsTab from '@/modules/users/composables/useDocumentsTab'
	import { User } from '@/modules/users/interfaces/user'

	interface Props {
		user: User
	}

	const props = defineProps<Props>()

	const {
		form,
		fileMetadata,
		isLoading,
		handleSave,
		handleUpdate,
		handleDelete,
	} = useDocumentsTab(props.user)

	export type UserDocumentsForm = typeof form

	const { user: userLogged } = useAuthUser()
</script>

<template>
	<RoundedBox class="bg-white">
		<div
			v-if="!isLoading"
			class="space-y-6"
		>
			<div class="border-b border-gray-200 pb-5">
				<h2 class="text-2xl font-semibold text-gray-900">
					Documentos de viaje
				</h2>
				<p class="mt-2 text-sm text-gray-600">
					Sube y gestiona los documentos de viaje. Los cambios se guardarán al
					hacer clic en "Guardar".
				</p>
			</div>

			<div class="grid grid-cols-1 gap-8">
				<div
					class="bg-gray-50 border border-gray-200 rounded-xl p-6 hover:shadow-md transition-shadow"
				>
					<StyledDatedMediaInput
						v-if="userLogged?.id !== props.user.id"
						media-key="id"
						v-model:file="form.id.file"
						v-model:expiresAt="form.id.expiresAt"
						label="Identificación oficial"
						:accept-types="['image/png', 'image/jpeg', 'image/jpg']"
						:max-kb="20480"
						:media="fileMetadata.id"
						:save-media="handleSave"
						@update:media="handleUpdate"
						@delete:media="handleDelete"
						:name-permission-create="'create-documents'"
						:name-permission-update="'update-documents'"
						:name-permission-delete="'delete-documents'"
						:entity-type="'App\\Models\\User'"
					/>

					<StyledDatedMediaInput
						v-else
						media-key="id"
						v-model:file="form.id.file"
						v-model:expiresAt="form.id.expiresAt"
						label="INE/ID"
						:accept-types="['image/png', 'image/jpeg', 'image/jpg']"
						:max-kb="20480"
						:media="fileMetadata.id"
						:save-media="handleSave"
						@update:media="handleUpdate"
						@delete:media="handleDelete"
						:name-permission-create="''"
						:name-permission-update="''"
						:name-permission-delete="''"
						:entity-type="''"
					/>
				</div>

				<div
					class="bg-gray-50 border border-gray-200 rounded-xl p-6 hover:shadow-md transition-shadow"
				>
					<StyledDatedMediaInput
						v-if="userLogged?.id !== props.user.id"
						media-key="passport"
						v-model:file="form.passport.file"
						v-model:expiresAt="form.passport.expiresAt"
						label="Pasaporte"
						:accept-types="['image/png', 'image/jpeg', 'image/jpg']"
						:max-kb="20480"
						:media="fileMetadata.passport"
						:save-media="handleSave"
						@update:media="handleUpdate"
						@delete:media="handleDelete"
						:name-permission-create="'create-documents'"
						:name-permission-update="'update-documents'"
						:name-permission-delete="'delete-documents'"
						:entity-type="'App\\Models\\User'"
					/>

					<StyledDatedMediaInput
						v-else
						media-key="passport"
						v-model:file="form.passport.file"
						v-model:expiresAt="form.passport.expiresAt"
						label="Pasaporte"
						:accept-types="['image/png', 'image/jpeg', 'image/jpg']"
						:max-kb="20480"
						:media="fileMetadata.passport"
						:save-media="handleSave"
						@update:media="handleUpdate"
						@delete:media="handleDelete"
						:name-permission-create="''"
						:name-permission-update="''"
						:name-permission-delete="''"
						:entity-type="''"
					/>
				</div>

				<div
					class="bg-gray-50 border border-gray-200 rounded-xl p-6 hover:shadow-md transition-shadow"
				>
					<StyledDatedMediaInput
						v-if="userLogged?.id !== props.user.id"
						media-key="driverLicense"
						v-model:file="form.driverLicense.file"
						v-model:expiresAt="form.driverLicense.expiresAt"
						label="Licencia de conducir"
						:accept-types="['image/png', 'image/jpeg', 'image/jpg']"
						:max-kb="20480"
						:media="fileMetadata.driverLicense"
						:save-media="handleSave"
						@update:media="handleUpdate"
						@delete:media="handleDelete"
						:name-permission-create="'create-documents'"
						:name-permission-update="'update-documents'"
						:name-permission-delete="'delete-documents'"
						:entity-type="'App\\Models\\User'"
					/>

					<StyledDatedMediaInput
						v-else
						media-key="driverLicense"
						v-model:file="form.driverLicense.file"
						v-model:expiresAt="form.driverLicense.expiresAt"
						label="Licencia de conducir"
						:accept-types="['image/png', 'image/jpeg', 'image/jpg']"
						:max-kb="20480"
						:media="fileMetadata.driverLicense"
						:save-media="handleSave"
						@update:media="handleUpdate"
						@delete:media="handleDelete"
						:name-permission-create="''"
						:name-permission-update="''"
						:name-permission-delete="''"
						:entity-type="''"
					/>
				</div>
			</div>
		</div>
	</RoundedBox>

	<LoadingModal :when="isLoading" />
</template>

<style scoped></style>
